import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";

import { ApiError } from "@/api/client";
import { Button, Card, Field, FormField, Input, Spinner } from "@/components/ui";
import { formatDateTime } from "@/lib/format";
import type { Mailbox } from "./queries";
import {
  useArmWebhook,
  useConnectMailbox,
  useDisconnectMailbox,
  useMailbox,
  useRenameSender,
} from "./queries";

/**
 * Every outcome the OAuth callback can redirect back with (.docs/09 §8.2).
 *
 * A closed list, matched against a closed list: the server sends a code from
 * `connection.ConnectError` and nothing from the query string is ever shown as
 * text. An unknown code falls through to the generic line.
 */
const RESULTS = [
  "connected",
  "denied",
  "email_mismatch",
  "taken",
  "no_refresh_token",
  "folders_missing",
  "wrong_datacenter",
  "expired",
  "forged",
  "replayed",
  "session_lost",
  "other_user",
  "unavailable",
] as const;

type Result = (typeof RESULTS)[number];

function resultFromParam(raw: string | null): Result | null {
  return RESULTS.includes(raw as Result) ? (raw as Result) : raw ? "unavailable" : null;
}

/**
 * *Nombre del remitente* (D37): what recipients see beside the address.
 *
 * The CRM's own field. Zoho's send API takes no sender name, so the server
 * writes this one into every send — and what the seller's *webmail* sends
 * under stays Zoho's own setting, which is why the hint says "desde el CRM".
 */
function SenderName({ mailbox }: { mailbox: Mailbox }) {
  const { t } = useTranslation();
  const rename = useRenameSender();
  const [name, setName] = useState(mailbox.display_name);
  const typed = name.trim();

  return (
    <form
      className="space-y-2 border-2 border-rule p-3"
      onSubmit={(event) => {
        event.preventDefault();
        rename.mutate(typed);
      }}
    >
      <FormField
        label={t("mail.settings.sender.title")}
        hint={t("mail.settings.sender.help")}
      >
        {(id) => (
          <Input
            id={id}
            value={name}
            maxLength={100}
            onChange={(event) => {
              setName(event.target.value);
              rename.reset();
            }}
          />
        )}
      </FormField>
      {!mailbox.display_name ? (
        <p role="alert" className="text-xs text-brand-700">
          {t("mail.settings.sender.missing")}
        </p>
      ) : null}
      {rename.error instanceof ApiError ? (
        <p role="alert" className="text-xs text-brand-700">
          {rename.error.message}
        </p>
      ) : null}
      {rename.isSuccess ? (
        <p role="status" className="text-xs">
          {t("mail.settings.sender.saved")}
        </p>
      ) : null}
      <Button
        type="submit"
        disabled={!typed || typed === mailbox.display_name || rename.isPending}
      >
        {rename.isPending
          ? t("mail.settings.sender.saving")
          : t("mail.settings.sender.save")}
      </Button>
    </form>
  );
}

/**
 * *Correo* in Ajustes: the window where a seller connects their mailbox (D31).
 *
 * They type their address here and their password on Zoho's page — this form
 * has no password field and the API refuses one, because the CRM never holds a
 * credential it would then have to guard.
 */
export function MailboxCard() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const query = useMailbox();
  const connect = useConnectMailbox();
  const disconnect = useDisconnectMailbox();
  const arm = useArmWebhook();
  const [email, setEmail] = useState("");

  const result = resultFromParam(params.get("correo"));
  const mailbox = query.data ?? null;

  const start = () => {
    connect.mutate(email.trim(), {
      onSuccess: ({ authorize_url }) => {
        // A full navigation, not a router push: the next page is Zoho's.
        window.location.assign(authorize_url);
      },
    });
  };

  const error =
    connect.error instanceof ApiError
      ? connect.error.message
      : disconnect.error instanceof ApiError
        ? disconnect.error.message
        : arm.error instanceof ApiError
          ? arm.error.message
          : null;

  return (
    <Card title={t("mail.settings.title")}>
      <div className="space-y-3 p-4 text-sm">
        {result ? (
          <p
            role="status"
            className={result === "connected" ? "text-ink" : "text-brand-700"}
          >
            {t(`mail.result.${result}`)}{" "}
            <button
              type="button"
              className="underline"
              onClick={() =>
                setParams((previous) => {
                  const next = new URLSearchParams(previous);
                  next.delete("correo");
                  return next;
                })
              }
            >
              {t("common.close")}
            </button>
          </p>
        ) : null}

        {error ? (
          <p role="alert" className="text-brand-700">
            {error}
          </p>
        ) : null}

        {query.isPending ? <Spinner label={t("common.loading")} /> : null}

        {!query.isPending && mailbox === null ? (
          <form
            className="space-y-3"
            onSubmit={(event) => {
              event.preventDefault();
              start();
            }}
          >
            <p className="text-mute-600">{t("mail.settings.help")}</p>
            <FormField
              label={t("mail.settings.address")}
              hint={t("mail.settings.passwordNote")}
            >
              {(id) => (
                <Input
                  id={id}
                  type="email"
                  value={email}
                  required
                  autoComplete="email"
                  placeholder={t("mail.settings.addressExample")}
                  onChange={(event) => setEmail(event.target.value)}
                />
              )}
            </FormField>
            <Button
              type="submit"
              variant="primary"
              disabled={!email.trim() || connect.isPending}
            >
              {connect.isPending
                ? t("mail.settings.connecting")
                : t("mail.settings.connect")}
            </Button>
          </form>
        ) : null}

        {mailbox ? (
          <>
            {mailbox.status !== "connected" ? (
              <p role="alert" className="text-brand-700">
                {t(`mail.settings.status.${mailbox.status}`)}
              </p>
            ) : null}
            <dl className="border-2 border-rule">
              <Field label={t("mail.settings.address")}>{mailbox.email_address}</Field>
              <Field label={t("mail.settings.state")}>
                {t(`mail.settings.status.${mailbox.status}`)}
              </Field>
              <Field label={t("mail.settings.connectedSince")}>
                {formatDateTime(mailbox.connected_at)}
              </Field>
              <Field label={t("mail.settings.lastSync")}>
                {mailbox.last_synced_at
                  ? formatDateTime(mailbox.last_synced_at)
                  : t("mail.settings.never")}
              </Field>
              <Field label={t("mail.settings.activities")}>
                {mailbox.writes_activities
                  ? t("common.yes")
                  : t("mail.settings.activitiesOff")}
              </Field>
              {mailbox.outgoing_blocked ? (
                <Field label={t("mail.settings.sending")}>
                  <span className="text-brand-700">
                    {t("mail.settings.outgoingBlocked")}
                  </span>
                </Field>
              ) : null}
            </dl>
            {mailbox.status === "connected" ? <SenderName mailbox={mailbox} /> : null}
            {/* M5, the doorbell. Polling stays the source of truth (D34),
                so a mailbox without this is a minute behind, never blind. */}
            <div className="space-y-2 border-2 border-rule p-3">
              <h5 className="text-[15px]">{t("mail.settings.webhook.title")}</h5>
              <p className="text-xs text-mute-600">{t("mail.settings.webhook.help")}</p>
              <p className="text-xs">
                {mailbox.webhook_is_configured
                  ? t("mail.settings.webhook.configured", {
                      when: mailbox.last_webhook_at
                        ? formatDateTime(mailbox.last_webhook_at)
                        : t("mail.settings.never"),
                    })
                  : t("mail.settings.webhook.missing")}
              </p>
              <Button disabled={arm.isPending} onClick={() => arm.mutate()}>
                {t("mail.settings.webhook.arm")}
              </Button>
              {arm.data ? (
                <div className="space-y-1 border-2 border-dashed border-mute-400 p-2">
                  <p className="text-xs">{t("mail.settings.webhook.steps")}</p>
                  <code className="block break-all bg-mute-200 p-2 text-[11px]">
                    {arm.data.webhook_url}
                  </code>
                  <p className="text-[11px] text-mute-600">
                    {t("mail.settings.webhook.window")}
                  </p>
                </div>
              ) : null}
            </div>

            <div className="flex flex-wrap gap-3">
              {/* Reconnecting is the same flow, and the address is known — the
                  seller only has to sign in again. */}
              <Button
                disabled={connect.isPending}
                onClick={() =>
                  connect.mutate(mailbox.email_address, {
                    onSuccess: ({ authorize_url }) =>
                      window.location.assign(authorize_url),
                  })
                }
              >
                {t("mail.settings.reconnect")}
              </Button>
              <Button
                disabled={disconnect.isPending}
                onClick={() => disconnect.mutate()}
              >
                {t("mail.settings.disconnect")}
              </Button>
            </div>
            <p className="text-xs text-mute-600">{t("mail.settings.disconnectNote")}</p>
          </>
        ) : null}
      </div>
    </Card>
  );
}
